import React from 'react'
import { Region, XP_REQUEST_MODE, type ComponentProps, type LayoutData } from '@enonic/react-components'
import EventListPrototype from '/react4xp/components/prototype/EventList'
import type { EventCardProps } from '/react4xp/components/prototype/EventCard/types'
import type { NoEventsBoxProps } from '/react4xp/components/prototype/NoEventsBox/types'
import type { SubpageHeroFilter } from '/react4xp/components/prototype/SubpageHero/types'

/**
 * Event List layout (I99X-397), wrapping the styled prototype component.
 *
 * The prototype drives filtering and paging through `onChange`/`onPageChange`
 * callbacks, but the processor derives its result from the query string
 * (`?location=&date=&page=`). So the handlers here do not hold React state -
 * they rewrite the URL and let the server re-run the query, same approach as
 * the reference-list layout.
 */
type FilterOption = {
	value: string
	label: string
}

type EventListCard = {
	href: string | null
	title: string
	intro?: string
	image?: { src: string; alt?: string }
	dateLabel: string
	location?: string
	eventType?: string
	isEventOver: boolean
}

type Pagination = {
	currentPage: number
	totalPages: number
	pageSize: number
	totalItems: number
	hasPrevious: boolean
	hasNext: boolean
}

type EmptyState = {
	heading?: string
	body?: string
	cta?: { label: string; url: string; isExternal: boolean }
}

type EventListStrings = {
	locationFilter: string
	locationFilterAll: string
	dateFilter: string
	dateFilterUpcoming: string
	dateFilterPast: string
}

type EventListLayoutData = {
	overline?: string
	heading?: string
	intro?: string
	items?: EventListCard[]
	locations?: FilterOption[]
	selectedFilters?: { location: string | null; date: 'Upcoming' | 'Past' }
	strings?: EventListStrings
	pagination?: Pagination
	noUpcomingEvents?: boolean
	emptyState?: EmptyState
}

// Sentinel for the "no filter" choice - SelectButton/location filter has no
// empty state, so the unfiltered case needs an option of its own. Location
// values are resolved location titles (see event-list.ts), which can never
// collide with this sentinel in practice - same convention as reference-list.
const ALL_VALUE = 'all'

// Events are always internal content (the processor resolves `href` with
// myPageUrl), so no card is ever an external link. A card without an href
// (unresolvable content) is dropped rather than rendering a dead link.
function toEventCard(item: EventListCard): EventCardProps | null {
	if (!item.href) return null

	return {
		image: item.image,
		eventType: item.eventType,
		title: item.title,
		location: item.location,
		date: item.dateLabel,
		url: item.href,
		isExternal: false,
	}
}

export const EventList = ({ component, data, meta }: ComponentProps<LayoutData>) => {
	const {
		overline,
		heading,
		items = [],
		locations = [],
		selectedFilters,
		strings,
		pagination,
		noUpcomingEvents,
		emptyState,
	} = (data || {}) as EventListLayoutData

	// Content Studio renders the layout inside its own frame; navigating there would
	// reload the editor rather than the page, so filtering/paging is inert in edit mode.
	const isEditMode = meta?.mode === XP_REQUEST_MODE.EDIT

	function navigate(params: Record<string, string | null>) {
		if (isEditMode || typeof window === 'undefined') return

		const url = new URL(window.location.href)
		Object.keys(params).forEach((key) => {
			const value = params[key]
			if (value) {
				url.searchParams.set(key, value)
			} else {
				url.searchParams.delete(key)
			}
		})
		window.location.assign(url.toString())
	}

	// Any filter change sends the reader back to page 1 - keeping the old page number
	// would land them past the end of a smaller result set.
	const locationFilter: SubpageHeroFilter = {
		id: 'location',
		label: strings?.locationFilter || '',
		options: [
			{ value: ALL_VALUE, label: strings?.locationFilterAll || '' },
			...locations.map(({ value, label }) => ({ value, label })),
		],
		value: selectedFilters?.location || ALL_VALUE,
		onChange: (value: string) => navigate({ location: value === ALL_VALUE ? null : value, page: null }),
	}

	// The prototype's own "upcoming"/"past" values are lowercase, while the
	// processor's query param and returned `selectedFilters.date` are
	// "Upcoming"/"Past" - map between the two at the edges.
	const dateFilter: SubpageHeroFilter = {
		id: 'date',
		label: strings?.dateFilter || '',
		options: [
			{ value: 'upcoming', label: strings?.dateFilterUpcoming || '' },
			{ value: 'past', label: strings?.dateFilterPast || '' },
		],
		value: selectedFilters?.date === 'Past' ? 'past' : 'upcoming',
		onChange: (value: string) => navigate({ date: value === 'past' ? 'Past' : null, page: null }),
	}

	const noEventsBox: NoEventsBoxProps | undefined = noUpcomingEvents
		? {
				heading: emptyState?.heading,
				description: emptyState?.body,
				ctaLink: emptyState?.cta && {
					text: emptyState.cta.label,
					url: emptyState.cta.url,
					isExternal: emptyState.cta.isExternal,
				},
			}
		: undefined

	return (
		<>
			<EventListPrototype
				overline={overline}
				heading={heading || ''}
				locationFilter={locationFilter}
				dateFilter={dateFilter}
				events={items.map(toEventCard).filter((card): card is EventCardProps => card !== null)}
				currentPage={pagination?.currentPage || 1}
				totalPages={Math.max(pagination?.totalPages || 0, 1)}
				onPageChange={(page: number) => navigate({ page: page > 1 ? String(page) : null })}
				noEventsBox={noEventsBox}
			/>
			<Region name="main" data={component?.regions?.main?.components || []} meta={meta} />
		</>
	)
}
